<template>
  <b-modal
    :active.sync="isModalActive"
    :width="640"
    scroll="keep"
    :can-cancel="canCancel"
    @close="closeModal"
  >
    <div class="border border-blue-light rounded shadow bg-white p-4">
      <form class="w-full" @submit.prevent="save">
        <header class="font-thin uppercase text-blue-light modal-card-head_ mb-4">
          {{ $t('add_wbs_budget_link') }}
        </header>

        <div class="modal-card-body relative">
          <b-field
            :label="$t('select_certifier_head')"
            :message="certifierHeadError"
            :type="certifierHeadError ? 'is-danger' : ''"
          >
            <b-select
              v-model="selectedCertifierHeadId"
              expanded
              :loading="loadingCertifierHeads"
              :placeholder="$t('select')"
              :disabled="!certifierHeads.length && !loadingCertifierHeads"
            >
              <option
                v-for="head in certifierHeads"
                :key="head.id"
                :value="head.id"
              >
                {{ certifierHeadLabel(head) }}
              </option>
            </b-select>
          </b-field>

          <p
            v-if="!loadingCertifierHeads && !certifierHeads.length"
            class="text-xs text-grey mt-2"
          >
            {{ $t('no_results') }}
          </p>

          <b-loading :is-full-page="false" v-model="processingData" :can-cancel="false" />
        </div>

        <footer class="modal-card-foot_">
          <div class="w-full inline-flex justify-end gap-2">
            <v-button type="button" @click="closeModal">
              {{ $t('cancel') }}
            </v-button>
            <button
              class="btn btn-blue"
              type="submit"
              :disabled="processingData || !certifierHeads.length"
            >
              {{ $t('save') }}
            </button>
          </div>
        </footer>
      </form>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios'

/** Mismo criterio que chapters-list: type 1 = chapter_project */
const CHAPTER_TYPE_PROJECT = 1

export default {
  name: 'boq-create-link-modal',

  props: {
    projectId: {
      type: [String, Number],
      required: true,
    },
    /** Igual que manage-chapters → chapters-list */
    entityType: {
      type: String,
      default: 'project',
    },
  },

  data() {
    return {
      isModalActive: false,
      processingData: false,
      loadingCertifierHeads: false,
      canCancel: ['escape', 'x', 'outside'],
      selectedCertifierHeadId: null,
      certifierHeads: [],
      certifierHeadError: null,
    }
  },

  methods: {
    show() {
      this.resetForm()
      this.isModalActive = true
      this.loadCertifierHeads()
    },

    closeModal() {
      this.isModalActive = false
      this.resetForm()
    },

    resetForm() {
      this.selectedCertifierHeadId = null
      this.certifierHeadError = null
      this.processingData = false
    },

    async loadCertifierHeads() {
      this.loadingCertifierHeads = true
      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.projectId}/chapters`,
          { params: { entity_type: this.entityType } }
        )

        if (data && data.success) {
          const flat = this.flattenChapters(data.chapters || [])
          this.certifierHeads = flat.filter(
            (chapter) => Number(chapter.type) === CHAPTER_TYPE_PROJECT
          )
        } else {
          this.certifierHeads = []
          this.$notify.error('error_loading_data')
        }
      } catch (error) {
        this.certifierHeads = []
        this.$notify.error('error_loading_data')
      } finally {
        this.loadingCertifierHeads = false
      }
    },

    flattenChapters(nodes, result = []) {
      if (!nodes || !nodes.length) {
        return result
      }

      nodes.forEach((node) => {
        result.push({
          id: node.id,
          code: node.code,
          name: node.name,
          type: node.type,
          parent_id: node.parent_id,
          level: node.level,
        })

        if (node.children && node.children.length) {
          this.flattenChapters(node.children, result)
        }
      })

      return result
    },

    certifierHeadLabel(head) {
      if (!head) {
        return ''
      }
      if (head.code && head.name) {
        return `${head.code} — ${head.name}`
      }
      return head.name || head.code || `#${head.id}`
    },

    async save() {
      if (!this.selectedCertifierHeadId) {
        this.certifierHeadError = this.$t('select_certifier_head')
        return
      }

      this.processingData = true
      this.certifierHeadError = null

      try {
        const { data } = await axios.post(
          `/api/v2/project/${this.projectId}/boq-roots`,
          { chapter_id: this.selectedCertifierHeadId }
        )

        if (data && data.success) {
          this.$notify.success('save_successful')
          this.$emit('created', data.boq_root)
          this.closeModal()
          return
        }

        const msg = data.message || 'assignment_change_fail'
        this.$notify.error(this.$t(msg))
      } catch (error) {
        const msg = error.response?.data?.message || 'assignment_change_fail'
        this.$notify.error(this.$t(msg))
      } finally {
        this.processingData = false
      }
    },
  },
}
</script>
